import { useEffect, useState } from 'react'
import classNames from 'classnames'
import { IconCaretDownFilled, IconChevronLeft, IconChevronRight, IconCircleCheck, IconCircleX, IconLoader2, IconStarFilled } from '@tabler/icons-react'
import { BazaarvoiceReviewSortKey, ReviewData } from 'shared-types'
import { LoadingSpinner } from '~/components'
import { Button } from '~/components/Button'
import { BazaarVoiceProps } from '~/components/BazaarVoiceScript/BazaarVoiceScript.types'
import Typography, { TypographyTag, TypographyVariant } from '~/components/Typography'
import { useReviews } from '~/hooks/useReviews'
import styles from './ProductDashboardReviews.module.scss'
import BazaarVoiceScript from '../../../components/BazaarVoiceScript'

const REVIEWS_PER_PAGE = 8
const RATING_TIERS = [5, 4, 3, 2, 1]
const FEEDBACK_VOTES_STORAGE_KEY = 'reviewFeedbackVotes'

const readStoredFeedbackVotes = (): Record<string, boolean> => {
  try {
    const stored = localStorage.getItem(FEEDBACK_VOTES_STORAGE_KEY)

    if (!stored) {
      return {}
    }

    return JSON.parse(stored)
  } catch {
    return {}
  }
}

export const REVIEWS_SECTION_ID = 'product-reviews'

const SORT_LABELS: Record<BazaarvoiceReviewSortKey, string> = {
  Relevancy: 'Most Relevant',
  SubmissionTimeDesc: 'Most Recent',
  RatingDesc: 'Highest Rating',
  RatingAsc: 'Lowest Rating',
}

interface StarRatingProps {
  rating: number
  className?: string
  size?: number
}

const StarRating = ({ rating, className, size = 16 }: StarRatingProps) => {
  return (
    <div className={classNames(styles.stars, className)} aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => {
        return (
          <svg
            key={i}
            className={classNames(styles.star, { [styles.starFilled]: i < rating })}
            width={size}
            height={size}
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        )
      })}
    </div>
  )
}

export interface ProductDashboardReviewsProps {
  sku?: string
  reviewData?: ReviewData
  bazaarVoice?: BazaarVoiceProps
}

export const ProductDashboardReviews = ({ sku, reviewData: initialReviewData, bazaarVoice }: ProductDashboardReviewsProps) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [sort, setSort] = useState<BazaarvoiceReviewSortKey>(BazaarvoiceReviewSortKey.Relevancy)
  const [isBazaarvoiceLoaded, setIsBazaarvoiceLoaded] = useState(false)
  const [feedbackVotes, setFeedbackVotes] = useState<Record<string, boolean>>({})
  // Reviews voted on in this session. Their counts get a display-time +1 —
  // persisted votes from earlier visits are already included in the fetched counts.
  const [sessionVoteIds, setSessionVoteIds] = useState<Record<string, boolean>>({})
  const [pendingFeedbackId, setPendingFeedbackId] = useState<string | null>(null)
  const { reviews: fetchedReviews, total: fetchedTotalReviews, totalPages: fetchedTotalPages, isLoading, fetchReviews, submitFeedback } = useReviews(sku, REVIEWS_PER_PAGE)

  useEffect(() => {
    setFeedbackVotes(readStoredFeedbackVotes())
  }, [])

  useEffect(() => {
    const intervalId = setInterval(() => {
      const stars = document.querySelectorAll('.bv-reviews-product-rating-star')

      if (stars.length > 0) {
        setIsBazaarvoiceLoaded(true)
        clearInterval(intervalId)
      }
    }, 1000)

    return () => {
      clearInterval(intervalId)
    }
  }, [])

  const reviews = fetchedReviews.length > 0 ? fetchedReviews : (initialReviewData?.reviews ?? [])
  const totalPages = fetchedTotalPages > 0 ? fetchedTotalPages : (initialReviewData?.totalPages ?? 0)
  const totalReviews = fetchedTotalReviews > 0 ? fetchedTotalReviews : (initialReviewData?.total ?? 0)
  const averageRating = initialReviewData?.averageRating ?? 0
  const ratingDistribution = initialReviewData?.ratingDistribution ?? []
  const start = (currentPage - 1) * REVIEWS_PER_PAGE
  const showingEnd = Math.min(start + REVIEWS_PER_PAGE, totalReviews)

  const goToPage = (page: number) => {
    if (isLoading) {
      return
    }

    setCurrentPage(page)
    fetchReviews(page, sort)
  }

  const handleFeedback = async (reviewId: string, isPositive: boolean) => {
    if (feedbackVotes[reviewId] !== undefined || pendingFeedbackId !== null) {
      return
    }

    setPendingFeedbackId(reviewId)

    try {
      const isSuccess = await submitFeedback(reviewId, isPositive)

      if (isSuccess) {
        const nextVotes = { ...feedbackVotes, [reviewId]: isPositive }

        setFeedbackVotes(nextVotes)
        setSessionVoteIds((prev) => {
          return { ...prev, [reviewId]: true }
        })

        try {
          localStorage.setItem(FEEDBACK_VOTES_STORAGE_KEY, JSON.stringify(nextVotes))
        } catch {
          // Storage unavailable — the vote still applies for this session.
        }
      }
    } finally {
      setPendingFeedbackId(null)
    }
  }

  const openBazaarvoiceForm = () => {
    const stars = document.querySelectorAll<HTMLElement>('.bv-reviews-product-rating-star')
    const lastStar = stars[stars.length - 1]

    if (!lastStar) {
      return
    }

    lastStar.click()
  }

  const renderSortDropdown = () => {
    return (
      <Typography tag={TypographyTag.div} variant={TypographyVariant.BodySmall} className={styles.sortSelectWrapper}>
        <select
          className={styles.sortSelect}
          aria-label="Sort reviews"
          value={sort}
          disabled={isLoading || totalReviews === 0}
          onChange={(e) => {
            const newSort = e.target.value as BazaarvoiceReviewSortKey
            setSort(newSort)
            setCurrentPage(1)
            fetchReviews(1, newSort)
          }}
        >
          {Object.values(BazaarvoiceReviewSortKey).map((key) => {
            return (
              <option key={key} value={key}>
                {SORT_LABELS[key]}
              </option>
            )
          })}
        </select>
        <IconCaretDownFilled size={14} className={styles.sortSelectArrow} aria-hidden="true" />
      </Typography>
    )
  }

  const renderSummary = () => {
    if (averageRating == null) {
      return null
    }

    const totalCount = totalReviews || 1

    return (
      <>
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            <Typography tag={TypographyTag.h2} variant={TypographyVariant.Heading5} className={styles.heading}>
              Customer Reviews
            </Typography>
            <img
              src="https://apps.bazaarvoice.com/common/images/trustmark_icons/trustmark_en.png"
              alt="Bazaarvoice Authentic Reviews"
              className={styles.bvBadge}
            />
          </div>
          <Button
            size="xs"
            className={styles.writeReviewButton}
            disabled={!isBazaarvoiceLoaded}
            onClick={() => {
              openBazaarvoiceForm()
            }}
          >
            <Typography tag={TypographyTag.span} variant={TypographyVariant.BodySmallBold} className={styles.writeReviewLabel}>
              Add a Review
            </Typography>
          </Button>
        </div>

        <div className={styles.summary}>
          <div className={styles.summaryAverage}>
            <Typography tag={TypographyTag.p} variant={TypographyVariant.Heading1} className={styles.averageValue}>
              {averageRating.toFixed(1)}
            </Typography>
            <StarRating rating={Math.round(averageRating)} size={19} />
            <Typography tag={TypographyTag.p} variant={TypographyVariant.BodySmall} className={styles.reviewCountLabel}>
              {totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}
            </Typography>
          </div>

          <div className={styles.summaryBars}>
            {RATING_TIERS.map((star) => {
              const item = ratingDistribution.find((r) => {
                return r.rating === star
              })
              const count = item?.count ?? 0
              const fillPct = (count / totalCount) * 100

              return (
                <div key={star} className={styles.distributionRow}>
                  <div className={styles.distributionStarLabel}>
                    <Typography tag={TypographyTag.span} variant={TypographyVariant.BodySmall} className={styles.distributionStarNum}>
                      {star}
                    </Typography>
                    <IconStarFilled size={12} className={styles.distributionStarIcon} aria-hidden="true" />
                  </div>
                  <div className={styles.distributionTrack} role="presentation">
                    <div className={styles.distributionFill} style={{ width: `${fillPct}%` }} />
                  </div>
                  <Typography tag={TypographyTag.span} variant={TypographyVariant.BodySmall} className={styles.distributionCount}>
                    {count}
                  </Typography>
                </div>
              )
            })}
          </div>

          <div className={styles.summaryMeta}>
            <Typography tag={TypographyTag.p} variant={TypographyVariant.BodySmall} className={styles.verifiedText}>
              Verified purchases only.<br />Powered by Bazaarvoice.
            </Typography>
            {renderSortDropdown()}
          </div>
        </div>
      </>
    )
  }

  const renderPagination = () => {
    if (totalPages <= 1) {
      return null
    }

    return (
      <div className={styles.pagination}>
        <button
          className={styles.pageArrow}
          onClick={() => {
            goToPage(Math.max(1, currentPage - 1))
          }}
          disabled={currentPage === 1}
          aria-label="Previous page"
        >
          <IconChevronLeft size={14} aria-hidden="true" />
        </button>
        {Array.from({ length: totalPages }, (_, i) => {
          const page = i + 1

          return (
            <button
              key={page}
              className={classNames(styles.pageNumber, {
                [styles.pageNumberActive]: currentPage === page,
              })}
              onClick={() => {
                goToPage(page)
              }}
              aria-label={`Page ${page}`}
              aria-current={currentPage === page ? 'page' : undefined}
            >
              {page}
            </button>
          )
        })}
        <button
          className={styles.pageArrow}
          onClick={() => {
            goToPage(Math.min(totalPages, currentPage + 1))
          }}
          disabled={currentPage === totalPages}
          aria-label="Next page"
        >
          <IconChevronRight size={14} aria-hidden="true" />
        </button>
        <Typography tag={TypographyTag.span} variant={TypographyVariant.BodySmall} className={styles.pageInfo}>
          Showing {start + 1}–{showingEnd} of {totalReviews}
        </Typography>
      </div>
    )
  }

  return (
    <div id={REVIEWS_SECTION_ID} className={styles.root}>
      {renderSummary()}
      <div className={styles.gridWrapper}>
        {isLoading && (
          <div className={styles.loadingOverlay}>
            <IconLoader2 className={styles.spinner} size={48} aria-hidden="true" />
          </div>
        )}
        <div className={classNames(styles.grid, { [styles.gridLoading]: isLoading })} aria-busy={isLoading}>
        {reviews.map((review) => {
            const formattedDate = new Date(review.date).toLocaleDateString('en-AU', {
              month: 'long',
              year: 'numeric',
            })
            const vote = feedbackVotes[review.id]
            const voteCountBonus = sessionVoteIds[review.id] ? 1 : 0
            const isFeedbackDisabled = vote !== undefined || pendingFeedbackId === review.id

            return (
              <div key={review.id} className={styles.reviewCard}>
                <StarRating rating={review.rating} />
                <Typography tag={TypographyTag.p} variant={TypographyVariant.BodyRegularBold} className={styles.title}>
                  {review.title}
                </Typography>
                <div className={styles.meta}>
                  <Typography tag={TypographyTag.span} variant={TypographyVariant.BodySmallBold} className={styles.author}>
                    {review.author}
                  </Typography>
                  <span className={styles.bullet} aria-hidden="true">&bull;</span>
                  <Typography tag={TypographyTag.span} variant={TypographyVariant.BodySmall} className={styles.date}>
                    {formattedDate}
                  </Typography>
                </div>
                <Typography tag={TypographyTag.p} variant={TypographyVariant.BodyRegular} className={styles.text}>
                  {review.text}
                </Typography>
                {review.recommended != null && (
                  <div className={styles.recommended}>
                    {review.recommended ? (
                      <IconCircleCheck
                        className={classNames(styles.recommendedIcon, styles.recommendedIconYes)}
                        size={18}
                        stroke={2.5}
                        aria-hidden="true"
                      />
                    ) : (
                      <IconCircleX
                        className={classNames(styles.recommendedIcon, styles.recommendedIconNo)}
                        size={18}
                        stroke={2.5}
                        aria-hidden="true"
                      />
                    )}
                    <Typography
                      tag={TypographyTag.span}
                      variant={TypographyVariant.BodySmallBold}
                      className={styles.recommendedText}
                    >
                      {review.recommended ? 'Yes, I recommend this product.' : 'No, I don\'t recommend this product.'}
                    </Typography>
                  </div>
                )}
                <div className={styles.helpful}>
                  <Typography tag={TypographyTag.span} variant={TypographyVariant.BodySmall} className={styles.helpfulLabel}>
                    Helpful?
                  </Typography>
                  <button
                    className={classNames(styles.helpfulButton, {
                      [styles.helpfulButtonVoted]: vote === true,
                    })}
                    disabled={isFeedbackDisabled}
                    aria-label="Mark review as helpful"
                    onClick={() => {
                      handleFeedback(review.id, true)
                    }}
                  >
                    <span>👍</span>
                    <span>({review.helpfulCount + (vote === true ? voteCountBonus : 0)})</span>
                  </button>
                  <button
                    className={classNames(styles.helpfulButton, {
                      [styles.helpfulButtonVoted]: vote === false,
                    })}
                    disabled={isFeedbackDisabled}
                    aria-label="Mark review as not helpful"
                    onClick={() => {
                      handleFeedback(review.id, false)
                    }}
                  >
                    <span>👎</span>
                    <span>({review.notHelpfulCount + (vote === false ? voteCountBonus : 0)})</span>
                  </button>
                  {/* Hidden until review reporting is implemented. */}
                  {/* <button className={styles.reportButton}>
                    <IconFlag size={14} aria-hidden="true" />
                    Report
                  </button> */}
                </div>
              </div>
            )
          })}
        </div>
      </div>
      {renderPagination()}
      {bazaarVoice && (
        <>
          <BazaarVoiceScript {...bazaarVoice} />
          <div
            className={styles.hidden}
            data-bv-show='reviews'
            data-bv-product-id={bazaarVoice.productIdentifier}>
            <div className='flex item-center py-4'>
              <LoadingSpinner />
            </div>
          </div>
        </>
      )}
    </div>
  )
}
